import { useEffect, useMemo } from "react";
import {
  useListPatientDispositionsQuery,
  useListEDDQuery,
  useListPatientMilestonesQuery,
  useListPatientClinicalInsightsQuery,
  useListIntelligenceTextQuery,
  useListMedicallyReadyStatusQuery,
  useListPatientInsightsQuery,
  useListPatientNotesQuery,
} from "../api/queries";
import PatientListEntry from "../types/PatientListEntry";
import { TypeChoices } from "../types/TypeChoices";
import { MilestoneStatusChoices } from "../types/MilestoneStatusChoices";
import { usePatientListCache } from "./use-patient-list-cache";
import { useQueryClient } from "react-query";

interface UsePatientListDetailsResult {
  data: PatientListEntry[];
  isLoading: boolean;
}

// Module-level constants so defaults don't change reference between renders
// (would otherwise re-trigger the seeding effect).
const EMPTY_LIST: never[] = [];
// The list endpoint always fetches without history, so seed with includeHistory=false
// to match the qcard's per-patient key shape.
const NO_HISTORY_SUFFIX = [false] as const;

export const usePatientListDetails = (
  patientListData: PatientListEntry[]
): UsePatientListDetailsResult => {
  const patientIDs = useMemo(
    () => patientListData.map((p) => p.id),
    [patientListData]
  );

  // get all the details
  const patientDispositionsQuery = useListPatientDispositionsQuery(patientIDs);
  const eddQuery = useListEDDQuery(patientIDs);
  const patientMilestonesQuery = useListPatientMilestonesQuery(patientIDs);
  const patientClinicalInsightsQuery =
    useListPatientClinicalInsightsQuery(patientIDs);
  const intelligenceTextQuery = useListIntelligenceTextQuery(patientIDs);
  const medicallyReadyStatusQuery =
    useListMedicallyReadyStatusQuery(patientIDs);
  const patientInsightsQuery = useListPatientInsightsQuery(patientIDs);
  const patientNotesQuery = useListPatientNotesQuery(patientIDs);

  // Seed per-patient cache so qcard opens from drawer hit cache, not network.
  // Pass patientIDs + a default so patients without any data for a given field
  // still get a cache entry (otherwise qcard would fetch on open for them).
  usePatientListCache(
    eddQuery,
    "edd",
    patientIDs,
    EMPTY_LIST,
    NO_HISTORY_SUFFIX
  );
  usePatientListCache(
    patientDispositionsQuery,
    "patientdispositions",
    patientIDs,
    EMPTY_LIST,
    NO_HISTORY_SUFFIX
  );
  usePatientListCache(
    patientMilestonesQuery,
    "patientmilestones",
    patientIDs,
    EMPTY_LIST
  );
  usePatientListCache(
    patientClinicalInsightsQuery,
    "patientclinicalinsights",
    patientIDs,
    EMPTY_LIST
  );
  usePatientListCache(
    intelligenceTextQuery,
    "intelligence",
    patientIDs,
    EMPTY_LIST
  );
  usePatientListCache(
    medicallyReadyStatusQuery,
    "medicallyready",
    patientIDs,
    null
  );
  usePatientListCache(
    patientInsightsQuery,
    "patientinsights",
    patientIDs,
    null
  );
  usePatientListCache(patientNotesQuery, "notes", patientIDs, EMPTY_LIST);

  // Patient query has a different shape (single Patient per row, triple-string key) —
  // seed inline here so we can re-use this data as well
  const queryClient = useQueryClient();
  useEffect(() => {
    for (const row of patientListData) {
      queryClient.setQueryData(
        [
          "patient",
          row.emr_patient_id,
          row.emr_hospital_id,
          row.fhir_patient_id,
        ],
        row
      );
    }
  }, [patientListData, queryClient]);

  const allQueries = [
    patientDispositionsQuery,
    eddQuery,
    intelligenceTextQuery,
    medicallyReadyStatusQuery,
    patientMilestonesQuery,
    patientClinicalInsightsQuery,
    patientInsightsQuery,
    patientNotesQuery,
  ];

  const isLoading = allQueries.some((query) => query.isLoading);

  // Memoize the aggregated result
  const aggregatedData = useMemo(() => {
    // Get data even if some queries are still loading - use empty defaults
    const dispositions = patientDispositionsQuery.data ?? {};
    const edds = eddQuery.data ?? {};
    const milestones = patientMilestonesQuery.data ?? {};
    const clinicalInsights = patientClinicalInsightsQuery.data ?? {};
    const intelText = intelligenceTextQuery.data ?? {};
    const medicallyReady = medicallyReadyStatusQuery.data ?? {};
    const insights = patientInsightsQuery.data ?? {};
    const notes = patientNotesQuery.data ?? {};

    // loop through each patient record and set the values
    const patientRecords = patientListData.map((p) => {
      const patientEdds = edds[p.id] ?? [];
      const patientDispositions = dispositions[p.id] ?? [];

      const currentEDD =
        patientEdds.find((e) => e.edd_type === TypeChoices.USER_ENTERED) ??
        null;

      const suggestedEDD =
        patientEdds.find(
          (e) => e.edd_type === TypeChoices.SMART_SUGGESTION && !e.status
        ) ?? null;

      const currentDispositions = patientDispositions.filter(
        (d) => d.disposition_type === TypeChoices.USER_ENTERED
      );

      const suggestedDisposition =
        patientDispositions.find(
          (d) =>
            d.disposition_type === TypeChoices.SMART_SUGGESTION && !d.status
        ) ?? null;

      const incompleteMilestones =
        milestones[p.id]?.filter(
          (m) => m.milestone_status === MilestoneStatusChoices.INCOMPLETE
        ) ?? [];

      return {
        ...p,
        current_edd: currentEDD,
        suggested_edd: suggestedEDD,
        current_dispositions: currentDispositions,
        suggested_disposition: suggestedDisposition,
        intelligence_text: intelText[p.id] ?? [],
        milestones: incompleteMilestones,
        clinical_insights: clinicalInsights[p.id] ?? [],
        medically_ready_status: medicallyReady[p.id] ?? null,
        insights: insights[p.id] ?? null,
        notes: notes[p.id] ?? [],
      };
    });

    return patientRecords;
  }, [
    patientListData,
    patientDispositionsQuery.data,
    eddQuery.data,
    intelligenceTextQuery.data,
    patientMilestonesQuery.data,
    patientClinicalInsightsQuery.data,
    medicallyReadyStatusQuery.data,
    patientInsightsQuery.data,
    patientNotesQuery.data,
  ]);

  return { data: aggregatedData, isLoading };
};
